import { useEffect, useId, useRef, useState } from "react";
import type {
  EnvironmentUiContextResponse,
  EnvironmentUiContextSubject,
} from "../../../api/contracts/environments";
import { environmentContextDeadline, environmentContextExpired } from "./context-lifetime";

export type EnvironmentControlProps = {
  response: EnvironmentUiContextResponse | null;
  failed?: boolean;
  invalidRoute?: boolean;
  onSelect: (subject: EnvironmentUiContextSubject) => void;
  onSelectAll: () => void;
  onRefresh: () => void;
  onLoadMore?: () => void;
  onNavigate: (href: string) => void;
};

export function sameEnvironmentSubject(
  a: EnvironmentUiContextSubject | undefined,
  b: EnvironmentUiContextSubject | undefined,
) {
  return Boolean(
    a &&
    b &&
    a.originTenantId === b.originTenantId &&
    a.environmentId === b.environmentId &&
    a.generationId === b.generationId,
  );
}

export function formatEnvironmentTime(value: string | null): string {
  return value
    ? new Intl.DateTimeFormat("en", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZoneName: "short",
      }).format(new Date(value))
    : "Not yet observed";
}

/** Presentation only. The authorized context supplies locality; this view never infers it. */
export function environmentControlView(
  props: Pick<EnvironmentControlProps, "response" | "failed" | "invalidRoute">,
  now = Date.now(),
) {
  const { response } = props;
  const neutral = (name: string, detail: string, state: string) => ({
    name,
    location: "",
    detail,
    state,
    context: null as EnvironmentUiContextResponse | null,
  });
  if (props.invalidRoute)
    return neutral("Choose an environment", "This link is incomplete", "invalid");
  if (props.failed || response?.status === "error")
    return neutral("Environment unavailable", "Try again", "error");
  if (!response) return neutral("Resolving environment…", "", "resolving");
  if (environmentContextExpired(response, now))
    return neutral("Resolving environment…", "Refreshing access", "expired");
  if (response.status === "setup-required")
    return {
      ...neutral(
        "Environment setup required",
        "Connect your environment to continue",
        "setup-required",
      ),
      context: response,
    };
  const presentation = response.presentation;
  if (!presentation) {
    if (response.status === "environment-set")
      return {
        ...neutral(
          "All environments",
          response.directory.completeness === "complete"
            ? "Authorized environments"
            : "Partial coverage",
          "all",
        ),
        context: response,
      };
    return {
      ...neutral(
        response.directory.items.length ? "Choose an environment" : "No environments available",
        response.directory.items.length
          ? "Select where you want to work"
          : "No environments are available to your account",
        "empty",
      ),
      context: response,
    };
  }
  if (Date.parse(presentation.authority.validUntil) <= now)
    return neutral("Resolving environment…", "Refreshing access", "expired");
  const kind = Date.parse(presentation.validUntil) > now ? presentation.locality.kind : "unknown";
  const location = {
    local: "Local",
    remote: "Remote",
    "offline-snapshot": "Offline snapshot",
    unknown: "Location unconfirmed",
  }[kind];
  let detail = "Status unconfirmed";
  const operational = response.operationalStatus;
  if (kind === "offline-snapshot" && presentation.locality.kind === "offline-snapshot")
    detail = `Captured ${formatEnvironmentTime(presentation.locality.capturedAt)}`;
  else if (operational.state === "current") {
    const { health } = operational.data.health;
    const warnings: string[] = [];
    if (health.trust === "trust_failed" || health.trust === "revoked")
      warnings.push("Verification problem");
    if (health.connectivity === "offline") warnings.push("Disconnected");
    else if (health.connectivity === "degraded") warnings.push("Connection interrupted");
    if (
      Date.parse(operational.data.validUntil) <= now ||
      Date.parse(operational.data.health.validUntil) <= now ||
      health.freshness === "stale"
    )
      warnings.push("Updates out of date");
    else if (health.freshness === "aging") warnings.push("Updates delayed");
    else if (health.freshness === "never_observed") warnings.push("Awaiting first observation");
    detail = warnings.length
      ? warnings.join(" · ")
      : health.connectivity === "online"
        ? "Connected"
        : "Connection unconfirmed";
  }
  return {
    name: kind === "local" ? presentation.displayLabel : presentation.displayName,
    location,
    detail,
    state: kind,
    context: response,
  };
}

function useEnvironmentClock(response: EnvironmentUiContextResponse | null) {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const update = () => setNow(Date.now());
    update();
    const deadlines = [
      response?.validUntil,
      response?.presentation?.validUntil,
      response?.presentation?.authority.validUntil,
      response?.operationalStatus.state === "current"
        ? response.operationalStatus.data.validUntil
        : null,
      response?.operationalStatus.state === "current"
        ? response.operationalStatus.data.health.validUntil
        : null,
    ];
    const expiry = response ? environmentContextDeadline(response) : null;
    const times = deadlines.filter((value): value is string => Boolean(value)).map(Date.parse);
    if (expiry !== null) times.push(expiry);
    const timers = times.map((value) =>
      setTimeout(update, Math.min(2_147_483_647, Math.max(0, value - Date.now() + 1))),
    );
    window.addEventListener("focus", update);
    document.addEventListener("visibilitychange", update);
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener("focus", update);
      document.removeEventListener("visibilitychange", update);
    };
  }, [response]);
  return Math.max(now, Date.now());
}

function EnvironmentGlyph({ state }: { state: string }) {
  return (
    <svg
      className="ex-env-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      aria-hidden="true"
    >
      {state === "local" ? (
        <>
          <rect x="4" y="4" width="16" height="12" rx="2" />
          <path d="M2 20h20M9 16v4m6-4v4" />
        </>
      ) : state === "remote" ? (
        <>
          <rect x="4" y="3" width="16" height="8" rx="2" />
          <rect x="4" y="13" width="16" height="8" rx="2" />
          <path d="M7 7h2m-2 10h2" />
        </>
      ) : state === "offline-snapshot" ? (
        <>
          <rect x="4" y="4" width="16" height="17" rx="2" />
          <path d="M8 2v4m8-4v4M4 10h16m-12 4h8m-8 3h5" />
        </>
      ) : (
        <>
          <circle cx="12" cy="12" r="8" />
          <path d="M12 7v6m0 3v1" />
        </>
      )}
    </svg>
  );
}

/** Reused in the selector and directory page; rows come only from the context response. */
export function EnvironmentDirectory(props: EnvironmentControlProps & { now?: number }) {
  const clock = useEnvironmentClock(props.response);
  const view = environmentControlView(props, props.now ?? clock);
  const [query, setQuery] = useState("");
  const searchId = useId();
  const context = view.context;
  const rows =
    context?.directory.items.filter((row) =>
      row.displayName.toLocaleLowerCase().includes(query.toLocaleLowerCase().trim()),
    ) ?? [];
  return (
    <div className="ex-env-directory" data-environment-directory="">
      {context && context.directory.items.length > 0 ? (
        <>
          <label className="ex-env-search-label" htmlFor={searchId}>
            Find an environment
          </label>
          <input
            id={searchId}
            className="ex-env-search"
            type="search"
            placeholder="Search environments…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <button
            className="ex-env-all"
            type="button"
            aria-pressed={context.selection?.kind === "environment-set"}
            onClick={props.onSelectAll}
          >
            <span>All environments</span>
            <span>View across environments</span>
          </button>
          <ul className="ex-env-list" aria-label="Available environments">
            {rows.map((row) => {
              const active = sameEnvironmentSubject(row.subject, context.presentation?.subject);
              return (
                <li key={JSON.stringify(row.subject)}>
                  <button
                    type="button"
                    className="ex-env-option"
                    aria-pressed={active}
                    onClick={() => props.onSelect(row.subject)}
                  >
                    <EnvironmentGlyph state={active ? view.state : "unknown"} />
                    <span className="ex-env-option-copy">
                      <span className="ex-env-option-name">
                        {active ? view.name : row.displayName}
                      </span>
                      <span className="ex-env-option-meta">
                        {active ? view.location : "Location unconfirmed"}
                        {row.stageLabel ? ` · ${row.stageLabel}` : ""}
                      </span>
                      <span className="ex-env-option-meta">
                        {active
                          ? view.detail
                          : `Last observed: ${formatEnvironmentTime(row.lastProjectionAt)}`}
                      </span>
                    </span>
                    {active ? <span className="ex-env-selected">Selected</span> : null}
                  </button>
                </li>
              );
            })}
          </ul>
          {rows.length === 0 ? (
            <p className="ex-env-empty" role="status">
              No environments match “{query}”. Try another name.
            </p>
          ) : null}
          {context.directory.completeness !== "complete" || context.directory.nextCursor ? (
            <>
              <p className="ex-env-note">
                Showing part of your authorized directory. Refresh to check for updates.
              </p>
              {context.directory.nextCursor && props.onLoadMore ? (
                <button
                  type="button"
                  className="ex-env-secondary ex-env-load-more"
                  onClick={props.onLoadMore}
                  disabled={props.response === null || props.failed}
                >
                  Load more environments
                </button>
              ) : null}
            </>
          ) : null}
          <p className="ex-env-note">
            Changing this view keeps running work on its original environment.
          </p>
        </>
      ) : (
        <div className="ex-env-empty">
          <EnvironmentGlyph state={view.state} />
          <h3>{view.name}</h3>
          <p>{view.detail || "Checking which environments you can access."}</p>
          {view.state === "setup-required" && context?.setup ? (
            <button
              type="button"
              className="ex-env-primary"
              onClick={() => props.onNavigate(context.setup!.nextHref)}
            >
              Set up environment
            </button>
          ) : null}
          {view.state !== "resolving" && view.state !== "setup-required" ? (
            <button type="button" className="ex-env-secondary" onClick={props.onRefresh}>
              Try again
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

export function EnvironmentControl(props: EnvironmentControlProps) {
  const now = useEnvironmentClock(props.response);
  const view = environmentControlView(props, now);
  const dialog = useRef<HTMLDialogElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const titleId = useId();
  const dialogId = useId();
  const label = [view.name, view.location, view.detail].filter(Boolean).join(" · ");
  const close = () => {
    dialog.current?.close();
    setOpen(false);
    trigger.current?.focus();
  };
  const act = (callback: () => void) => {
    close();
    callback();
  };
  return (
    <div className="ex-env-control" data-environment-control={view.state}>
      <button
        ref={trigger}
        type="button"
        className="ex-env-trigger"
        aria-label={`Environment: ${label}. Change environment`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={dialogId}
        onClick={() => {
          dialog.current?.showModal();
          setOpen(true);
        }}
      >
        <EnvironmentGlyph state={view.state} />
        <span className="ex-env-trigger-copy">
          <span className="ex-env-trigger-main">
            <span className="ex-env-name" title={view.name}>
              {view.name}
            </span>
            {view.location ? <span className="ex-env-location">· {view.location}</span> : null}
          </span>
          {view.detail ? <span className="ex-env-detail">{view.detail}</span> : null}
        </span>
        <span className="ex-env-chevron" aria-hidden="true">
          ⌄
        </span>
      </button>
      <span className="ex-env-sr" role="status" aria-live="polite" aria-atomic="true">
        {label}
      </span>
      <dialog
        ref={dialog}
        id={dialogId}
        className="ex-env-dialog"
        aria-labelledby={titleId}
        onCancel={(event) => {
          event.preventDefault();
          close();
        }}
        onClose={() => {
          setOpen(false);
          trigger.current?.focus();
        }}
        onKeyDown={(event) => {
          if (event.key !== "Tab") return;
          const controls = Array.from(
            event.currentTarget.querySelectorAll<HTMLElement>(
              'button:not(:disabled), input:not(:disabled), a[href], [tabindex="0"]',
            ),
          );
          const first = controls[0];
          const last = controls[controls.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
        }}
      >
        <div className="ex-env-dialog-header">
          <div>
            <p className="ex-env-eyebrow">Your working context</p>
            <h2 id={titleId}>Environments</h2>
          </div>
          <button
            type="button"
            className="ex-env-close"
            aria-label="Close environment selector"
            onClick={close}
          >
            ×
          </button>
        </div>
        <EnvironmentDirectory
          {...props}
          now={now}
          onSelect={(subject) => act(() => props.onSelect(subject))}
          onSelectAll={() => act(props.onSelectAll)}
          onNavigate={(href) => act(() => props.onNavigate(href))}
        />
        <div className="ex-env-dialog-footer">
          <button
            type="button"
            className="ex-env-secondary"
            onClick={() => act(() => props.onNavigate("/environments"))}
          >
            Manage environments
          </button>
          <button type="button" className="ex-env-secondary" onClick={props.onRefresh}>
            Refresh
          </button>
        </div>
      </dialog>
    </div>
  );
}
